import { transformRecordToOption } from '@/utils/common';
import type {
  AppointmentRecordStatusEnum,
  DepartmentTypeEnum,
  DictTypeEnum,
  OrderCreditPaymentTypeEnum,
  OrderPaymentTypeEnum,
  PlatformEnum,
  ProductTypeEnum,
  RoleTypeEnum,
  StoreCommissionType,
  TransactionFlowEnum,
  TransactionTypeEnum,
  WalletTypeEnum
} from '@/enum';
import {
  EffectiveRangeEnum,
  NotificationActionEnum,
  NotificationTypeEnum,
  ProductEffectiveRangeEnum,
  StoreAppointmentEventEnum,
  StoreAppointmentStateEnum,
  StoreCustomerStatusEnum,
  StoreEmployeeTypeEnum,
  U8CCCodeEnum,
  VehicleCategoryEnum
} from '@/enum';
import type { Api } from '@/typings/api';
import type { CommonType } from '@/typings/common';

/** 字段类型 */
export const FieldTypeRecord: Record<Api.Gen.FieldType, string> = {
  String: 'String',
  Boolean: 'Boolean',
  Integer: 'Integer',
  BigDecimal: 'BigDecimal',
  Text: 'Text',
  Date: 'Date',
  Long: 'Long',
  OneToOne: 'OneToOne',
  OneToMany: 'OneToMany',
  ManyToOne: 'ManyToOne',
  ManyToMany: 'ManyToMany'
};
export const FieldTypeOptions = transformRecordToOption(FieldTypeRecord);

/** 级联关系 */
export const JoinCascadeRecord: Record<Api.Gen.JoinCascade, string> = {
  ALL: 'ALL',
  PERSIST: 'PERSIST',
  MERGE: 'MERGE',
  REMOVE: 'REMOVE',
  REFRESH: 'REFRESH',
  DETACH: 'DETACH'
};
export const JoinCascadeOptions = transformRecordToOption(JoinCascadeRecord);

/** 平台 */
export const PlatformRecord: Record<PlatformEnum, string> = {
  1000: '睿和云店'
};
export const PlatformOptions = transformRecordToOption(PlatformRecord, 'number');

/** 字典类型 */
export const dictTypeRecord: Record<DictTypeEnum, CommonType.TagOption<string>> = {
  CONTRACT_TYPE: {
    label: '合同类型',
    config: {
      /** 是否树形结构展示 */
      isTree: false
    }
  },
  PRODUCT_NAME: {
    label: '产品品牌',
    config: {
      isTree: false
    }
  },
  PRODUCT_VERSION: {
    label: '产品系列',
    config: {
      isTree: false
    }
  },
  PRODUCT_SPEC: {
    label: '产品型号',
    config: {
      isTree: false
    }
  },
  PRODUCT_UNIT: {
    label: '产品单位',
    config: {
      isTree: false
    }
  },
  PRODUCT_SALE_UNIT: {
    label: '产品销售单位',
    config: {
      isTree: false
    }
  },
  SUPPLIER_VERSION: {
    label: '供应商型号',
    config: {
      isTree: false
    }
  },
  COURSE_CATEGORY: {
    label: '课程大类',
    config: {
      isTree: false
    }
  },
  CONSTRUCTION_SITE: {
    label: '施工部位',
    config: {
      isTree: true
    }
  },
  PAYMENT_TYPE: {
    label: '支付方式',
    config: {
      isTree: false
    }
  },
  PACKAGE_NOT_UPGRADE: {
    label: '套餐未升级类型',
    config: {
      isTree: false
    }
  },
  APPOINTMENT_LOSS_TYPE: {
    label: '预约流失类型',
    config: {
      isTree: false
    }
  },
  CUSTOMER_SOURCE: {
    label: '客户来源',
    config: {
      isTree: false
    }
  },
  MINIAPP_CHANNEL: {
    label: '小程序渠道',
    config: {
      isTree: false
    }
  },
  U8_DEPARTMENT: {
    label: 'U8部门',
    config: {
      isTree: false
    }
  },
  CM_CATEGORY: {
    label: '配件类型',
    config: {
      isTree: false
    }
  }
};
export const dictTypeOptions = transformRecordToOption(dictTypeRecord, 'string');

/** 用户状态 */
export const accountStatusRecord: Record<Api.Sys.AccountStatus, CommonType.TagOption<number>> = {
  1: {
    label: '已激活',
    color: 'success'
  },
  2: {
    label: '已禁用',
    color: 'error'
  },
  3: {
    label: '未激活',
    color: 'warning'
  }
};

export const accountStatusOptions = transformRecordToOption(accountStatusRecord, 'number');

/** 性别 */
export const accountGenderRecord: Record<Api.Sys.AccountGender, string> = {
  0: '未知',
  1: '男',
  2: '女'
};
export const accountGenderOptions = transformRecordToOption(accountGenderRecord, 'number');

/** 菜单类型 */
export const menuTypeRecord: Record<Api.Sys.MenuType, string> = {
  0: '目录',
  1: '菜单',
  2: '资源'
};
export const menuTypeOptions = transformRecordToOption(menuTypeRecord, 'number');

/** 角色类型 */
export const roleTypeRecord: Record<RoleTypeEnum, string> = {
  0: '系统角色',
  1: '平台角色',
  2: '组织角色',
  3: '门店角色',
  4: '合作门店角色',
  5: '主机厂项目组',
  6: '4S店角色',
  7: '普通客户',
  8: '经销商角色'
};
export const roleTypeOptions = transformRecordToOption(roleTypeRecord, 'number');

/** 组织类型 */
export const DepartmentTypeRecord: Record<DepartmentTypeEnum, string> = {
  1: '平台',
  3: '门店',
  4: '合作门店',
  5: '主机厂项目组',
  6: '4S店',
  7: '经销商'
};
export const DepartmentTypeOptions = transformRecordToOption(DepartmentTypeRecord, 'number');

/** 提交状态 */
export const submitStatusRecord: Record<number, string> = {
  0: '暂存',
  1: '提交'
};
export const submitStatusOptions = transformRecordToOption(submitStatusRecord, 'number');

/** 数据规则组多规则合并方式 */
export const DataFilterMergeTypeRecord: Record<number, string> = {
  1: 'AND',
  2: 'OR'
};
export const DataFilterMergeTypeOptions = transformRecordToOption(DataFilterMergeTypeRecord, 'number');

/** 数据规则过滤类型 */
export const DataFilterFilterTypeRecord: Record<string, string> = {
  andEq: '=',
  andNeq: '!=',
  andGt: '>',
  andGte: '>=',
  andLt: '<',
  andLte: '<=',
  andIn: 'IN',
  andNotIn: 'NOT IN'
};
export const DataFilterFilterTypeOptions = transformRecordToOption(DataFilterFilterTypeRecord, 'string');

/** 数据值类型 */
export const DataFilterValueTypeRecord: Record<number, string> = {
  1: '固定值',
  2: '变量值'
};
export const DataFilterValueTypeOptions = transformRecordToOption(DataFilterValueTypeRecord, 'number');

export const DataFilterVariableRecord: Record<string, string> = {
  currentUser: '当前用户',
  childUser: '下级用户',
  currentAndChildUser: '当前用户和下级用户',
  currentCustomer: '当前客户',
  childCustomer: '下级客户',
  currentAndChildCustomer: '当前客户和下级客户',
  currentDepartment: '当前组织',
  childDepartment: '下级组织',
  currentAndChildDepartment: '当前组织和下级组织'
};
export const DataFilterVariableOptions = transformRecordToOption(DataFilterVariableRecord, 'string');

/** 产品类型 */
export const productTypeNumberRecord: Record<number, string> = {
  1: '窗膜',
  2: '车衣',
  3: '改色膜'
};
export const productTypeRecord: Record<ProductTypeEnum, string> = {
  1: '窗膜',
  2: '车衣',
  3: '改色膜'
};
export const productTypeOptions = transformRecordToOption(productTypeRecord, 'number');

/** 题目类型 */
export const questionTypeRecord: Record<number, string> = {
  1: '单选',
  2: '多选',
  3: '填空'
};
export const questionTypeOptions = transformRecordToOption(questionTypeRecord, 'number');

/** 车膜产品范围类型 */
export const productRangeTypeRecord: Record<number, string> = {
  1: '上游',
  2: '本店'
};

export const productRangeTypeOptions = transformRecordToOption(productRangeTypeRecord, 'number');

/** 客户跟踪状态 */
export const userTrackingStatusRecord: Record<number, string> = {
  0: '待确认',
  10: '待签字',
  20: '待开单',
  30: '已开单',
  40: '已完成',
  '-10': '已取消'
};
export const userTrackingStatusOptions = transformRecordToOption(userTrackingStatusRecord, 'number');

/** 问卷状态 */
export const surveyStatusRecord: Record<number, string> = {
  0: '未完成',
  10: '已完成'
};
export const surveyStatusOptions = transformRecordToOption(surveyStatusRecord, 'number');

/** 交易流向 */
export const TransactionFlowRecord: Record<TransactionFlowEnum, string> = {
  1: '入账',
  2: '出账'
};
export const TransactionFlowOptions = transformRecordToOption(TransactionFlowRecord, 'number');

/** 交易类型 */
export const TransactionTypeRecord: Record<TransactionTypeEnum, string> = {
  1: '工时提成',
  2: '车膜提成',
  3: '商品提成',
  4: '订单提成',
  5: '合伙人提成',
  6: '4S店提成',
  10: '服务官提成',
  11: '推荐官提成',
  12: '客户经理提成',
  100: '员工提现'
};
export const TransactionTypeOptions = transformRecordToOption(TransactionTypeRecord, 'number');

/** 客户类型 */
export const CustomerTypeRecord: Record<number, string> = {
  1: '个人',
  2: '公司'
};
export const CustomerTypeOptions = transformRecordToOption(CustomerTypeRecord, 'number');

/** 采购模式 */
export const ProcurementModeRecord: Record<number, string> = {
  1: '直营模式',
  2: '代理模式'
};
export const ProcurementModeOptions = transformRecordToOption(ProcurementModeRecord, 'number');

/** 库存状态 */
export const InventoryStatusRecord: Record<number, string> = {
  1: '在库',
  2: '待出库',
  3: '已出库'
};
export const InventoryStatusOptions = transformRecordToOption(InventoryStatusRecord, 'number');

/** 库存状态 */
export const InventoryPurchaseModeRecord: Record<number, string> = {
  1: '系统采购',
  2: '门店自采'
};
export const InventoryPurchaseModeOptions = transformRecordToOption(InventoryPurchaseModeRecord, 'number');

/** 订单状态 */
export const OrderStatusRecord: Record<number, string> = {
  '-1': '已退回',
  1: '暂存',
  2: '待审核',
  3: '签订合同',
  4: '待付款',
  5: '付款凭证审核',
  6: '待出库',
  7: '待收货',
  8: '已完成'
};
export const OrderStatusOptions = transformRecordToOption(OrderStatusRecord, 'number');

/** 货物明细状态 */
export const OrderGoodsItemStatusRecord: Record<number, string> = {
  1: '待入库',
  2: '已入库'
};
export const OrderGoodsItemStatusOptions = transformRecordToOption(OrderGoodsItemStatusRecord, 'number');

/** 门店状态 */
export const StoreStatusRecord: Record<number, CommonType.TagOption<number>> = {
  0: {
    label: '待审核',
    color: 'warning'
  },
  1: {
    label: '通过',
    color: 'success'
  },
  2: {
    label: '未通过',
    color: 'error'
  }
};
export const StoreStatusOptions = transformRecordToOption(StoreStatusRecord, 'number');

/** 客户状态 */
export const customerInfoStatus: Record<number, string> = {
  1: '启用',
  2: '停用'
};
export const customerInfoOptions = transformRecordToOption(customerInfoStatus, 'number');

/** 钱包类型 */
export const walletTypeRecord: Record<WalletTypeEnum, string> = {
  1: '用户',
  2: '客户',
  3: '员工',
  4: '合伙人',
  5: '4S店'
};
export const walletTypeOptions = transformRecordToOption(walletTypeRecord, 'number');

/** 支付方式 */
export const paymentMethodRecord: Record<number, string> = {
  1: '微信',
  2: '支付宝',
  3: '银行打款',
  4: '挂账'
};
export const paymentMethodOptions = transformRecordToOption(paymentMethodRecord, 'number');

/** 车膜采购挂账订单支付方式 */
export const paymentCreditMethodRecord: Record<number, string> = {
  1: '微信',
  2: '支付宝',
  3: '银行打款'
};
export const paymentCreditMethodOptions = transformRecordToOption(paymentCreditMethodRecord, 'number');

/** 车膜销售子订单状态枚举 */
export const FilmPurchaseChildOrderStatusRecord: Record<number, string> = {
  10: '待处理',
  20: '待出库',
  21: '出库中',
  30: '待入库',
  100: '已完成'
};

export const FilmPurchaseChildOrderStatusOptions = transformRecordToOption(
  FilmPurchaseChildOrderStatusRecord,
  'number'
);

/** 车膜采购订单状态 */
export const FilmPurchaseOrderStatusRecord: Record<number, string> = {
  10: '待提交',
  '-10': '退回',
  '-20': '撤销',
  20: '待审核',
  25: '待甲方签订合同',
  30: '待乙方签订合同',
  40: '待收款',
  50: '待采购',
  60: '采购中',
  70: '待出库',
  71: '出库中',
  75: '线下待入库',
  80: '待入库',
  100: '已完成'
};

export const FilmPurchaseOrderStatusOptions = transformRecordToOption(FilmPurchaseOrderStatusRecord, 'number');

/** 发货方式 */
export const deliveryTypeRecord: Record<number, string> = {
  1: '工厂发货',
  2: '总部发货'
};
export const deliveryTypeOptions = transformRecordToOption(deliveryTypeRecord, 'number');

/** 章节类型 */
export const chapterTypeRecord: Record<number, string> = {
  1: '视频',
  2: '文本'
};
export const chapterTypeOptions = transformRecordToOption(chapterTypeRecord, 'number');

/** 订单状态 */
export const WarrantyOrderStatusRecord: Record<number, string> = {
  // '-1': '已退回',
  // 1: '暂存',
  // 2: '待确认',
  // 3: '待申请',
  // 4: '待审核',
  // 5: '已完成'
  1: '待完善',
  2: '已完成'
};
export const WarrantyOrderStatusOptions = transformRecordToOption(WarrantyOrderStatusRecord, 'number');

/** 领用归还类型 */
export const materialTransactionOperationTypeRecord: Record<number, string> = {
  1: '领用',
  2: '归还'
};

export const MaterialTransactionOperationTypeOptions = transformRecordToOption(
  materialTransactionOperationTypeRecord,
  'number'
);

/** 库存分类 */
export const InventoryTypeRecord: Record<number, string> = {
  1: '车膜',
  2: '商品'
};
export const CheckType = transformRecordToOption(InventoryTypeRecord, 'number');

/** 质保类型 */
export const WarrantyTypeStatusRecord: Record<number, string> = {
  1: '服务',
  2: '产品'
};
export const WarrantyTypeStatusOptions = transformRecordToOption(WarrantyTypeStatusRecord, 'number');

/** 出库单出库状态 */
export const OutboundStatusRecord: Record<number, string> = {
  '-1': '已作废',
  0: '待出库',
  1: '已出库',
  2: '出库中'
};
export const OutboundStatusOptions = transformRecordToOption(OutboundStatusRecord, 'number');

export const OutboundTypeRecord: Record<number, string> = {
  1: '领料',
  2: '补料'
};
export const OutboundTypeOptions = transformRecordToOption(OutboundTypeRecord, 'number');
/** 评价状态 */
export const EvaluationStatusRecord: Record<number, string> = {
  0: '未评价',
  1: '已评价'
};
export const EvaluationStatusOptions = transformRecordToOption(EvaluationStatusRecord, 'number');

/** 评价对象类型 */
export const AccountTypeRecord: Record<number, string> = {
  1: '技师',
  2: '销售'
};
export const AccountTypeOptions = transformRecordToOption(AccountTypeRecord, 'number');

/** 预约状态 */
export const AppointmentStatusRecord: Record<StoreAppointmentStateEnum, CommonType.TagOption<number>> = {
  [StoreAppointmentStateEnum.EXPIRED]: {
    label: '已过期',
    color: 'error'
  },
  [StoreAppointmentStateEnum.LOSS]: {
    label: '已流失',
    color: 'error'
  },
  [StoreAppointmentStateEnum.WAIT_AUDIT_LOSS]: {
    label: '预约流失审核',
    color: 'warning'
  },
  [StoreAppointmentStateEnum.WAIT_AUDIT_CANCEL]: {
    label: '预约取消审核',
    color: 'warning'
  },
  [StoreAppointmentStateEnum.WAIT_ALLOCATION]: {
    label: '待分配',
    color: 'warning'
  },
  [StoreAppointmentStateEnum.WAIT_APPOINTMENT]: {
    label: '待预约',
    color: 'warning'
  },
  [StoreAppointmentStateEnum.ON_APPOINTMENT]: {
    label: '预约中',
    color: 'warning'
  },
  [StoreAppointmentStateEnum.WAIT_AUDIT_EDIT]: {
    label: '预约修改审核',
    color: 'warning'
  },
  [StoreAppointmentStateEnum.WAIT_VERIFIED]: {
    label: '待核销',
    color: 'warning'
  },
  [StoreAppointmentStateEnum.IN_CONSTRUCTION]: {
    label: '施工中',
    color: 'warning'
  },
  [StoreAppointmentStateEnum.DONE]: {
    label: '已完成',
    color: 'success'
  }
};
export const AppointmentStatusOptions = transformRecordToOption(AppointmentStatusRecord, 'number');

/** 预约方式 */
export const AppointmentOrderTypeRecord: Record<number, string> = {
  1: '客户预约',
  2: '销售预约',
  3: '周期项目',
  4: '外部推荐',
  5: '合作门店',
  6: '主机厂',
  99: '其他'
};
export const AppointmentOrderTypeOptions = transformRecordToOption(AppointmentOrderTypeRecord, 'number');

/** 提成类别 */
export const CommissionTypeRecord: Record<StoreCommissionType, string> = {
  1: '工时提成',
  2: '车膜提成',
  3: '商品提成',
  4: '订单提成',
  5: '合伙人提成',
  6: '4S店提成'
};
export const CommissionTypeOptions = transformRecordToOption(CommissionTypeRecord, 'number');

/** 员工类别 */
export const EmployeeTypeRecord: Record<StoreEmployeeTypeEnum, string> = {
  [StoreEmployeeTypeEnum.TECHNICIAN]: '技师',
  [StoreEmployeeTypeEnum.SALE]: '销售',
  [StoreEmployeeTypeEnum.PARTNER]: '合伙人',
  [StoreEmployeeTypeEnum.VEHICLE]: '4S店'
};
export const EmployeeTypeOptions = transformRecordToOption(EmployeeTypeRecord, 'number');

/** 提成计算方式 */
export const CommissionCalcTypeRecord: Record<number, string> = {
  1: '固定值',
  2: '百分比'
};
export const CommissionCalcTypeOptions = transformRecordToOption(CommissionCalcTypeRecord, 'number');

/** 销售订单状态 */
export const storeSaleOrderStatusRecord: Record<number, CommonType.TagOption<number>> = {
  '-10': {
    label: '作废审核',
    color: 'warning'
  },
  '-1': {
    label: '作废',
    color: 'info'
  },
  0: {
    label: '开单',
    color: 'info'
  },
  1: {
    label: '派工',
    color: 'info'
  },
  2: {
    label: '施工中',
    color: 'warning'
  },
  3: {
    label: '已完工',
    color: 'primary'
  },
  4: {
    label: '验车',
    color: 'warning'
  },
  5: {
    label: '已完成',
    color: 'success'
  }
};

export const storeSaleOrderStatusOptions = transformRecordToOption(storeSaleOrderStatusRecord, 'number');

/** 销售订单服务项目状态 0:未开始(待派工),1:待领料,2:已领料,3:施工中,4:已完工 */
export const storeSaleOrderServiceItemStatusRecord: Record<number, CommonType.TagOption<number>> = {
  '-1': {
    label: '作废',
    color: 'info'
  },
  0: {
    label: '待派工',
    color: 'info'
  },
  1: {
    label: '待领料',
    color: 'warning'
  },
  2: {
    label: '已领料',
    color: 'warning'
  },
  3: {
    label: '施工中',
    color: 'warning'
  },
  4: {
    label: '已完工',
    color: 'success'
  }
};
export const storeSaleOrderServiceItemStatusOptions = transformRecordToOption(
  storeSaleOrderServiceItemStatusRecord,
  'number'
);

/** 销售订单整车服务状态 */
export const StoreSaleOrderFullVehicleStatusRecord: Record<number, string> = {
  0: '待派工',
  1: '已派工',
  2: '验车',
  3: '已领料',
  4: '施工中',
  5: '已完工'
};
export const StoreSaleOrderFullVehicleStatusOptions = transformRecordToOption(
  StoreSaleOrderFullVehicleStatusRecord,
  'number'
);

/** 采购订单状态 0:暂存，1:待审核，2:采购中，3:部分入库，4:已入库 */
export const storePurchaseOrderStatusRecord: Record<number, CommonType.TagOption<number>> = {
  0: {
    label: '暂存',
    color: 'info'
  },
  1: {
    label: '待审核',
    color: 'info'
  },
  2: {
    label: '采购中',
    color: 'warning'
  },
  3: {
    label: '部分入库',
    color: 'warning'
  },
  4: {
    label: '已入库',
    color: 'success'
  }
};
export const storePurchaseOrderStatusOptions = transformRecordToOption(storePurchaseOrderStatusRecord, 'number');

/** 销售订单支付方式 */
export const orderPaymentTypeRecord: Record<OrderPaymentTypeEnum, string> = {
  0: '挂账',
  1: '支付宝',
  2: '微信',
  3: '银行',
  4: '现金',
  5: '对公打款',
  6: 'DF收款',
  7: '积分',
  8: '美团',
  9: '抖音'
};
export const orderPaymentTypeOptions = transformRecordToOption(orderPaymentTypeRecord, 'number');

/** 销售挂账订单支付方式 */
export const orderCreditPaymentTypeRecord: Record<OrderCreditPaymentTypeEnum, string> = {
  1: '支付宝',
  2: '微信',
  3: '银行',
  4: '现金',
  5: '对公打款',
  6: 'DF收款',
  7: '积分',
  8: '美团',
  9: '抖音'
};
export const orderCreditPaymentTypeOptions = transformRecordToOption(orderCreditPaymentTypeRecord, 'number');

/** 车辆分类 */
export const vehicleCategoryRecord: Record<VehicleCategoryEnum, string> = {
  [VehicleCategoryEnum.SEDAN]: '轿车',
  [VehicleCategoryEnum.SUV]: 'SUV',
  [VehicleCategoryEnum.MPV]: 'MPV',
  [VehicleCategoryEnum.PICKUP]: '皮卡',
  [VehicleCategoryEnum.BUSINESS]: '商务车',
  [VehicleCategoryEnum.SPORTS]: '跑车',
  [VehicleCategoryEnum.OFFROAD]: '越野'
};
export const vehicleCategoryOptions = transformRecordToOption(vehicleCategoryRecord, 'number');

/** 作用范围 */
export const effectiveRangeRecord: Record<EffectiveRangeEnum, string> = {
  [EffectiveRangeEnum.ONCE]: '一次性',
  [EffectiveRangeEnum.LONG_TERM]: '长期'
};
export const effectiveRangeOptions = transformRecordToOption(effectiveRangeRecord, 'number');

/** SKU作用范围 */
export const productEffectiveRangeRecord: Record<ProductEffectiveRangeEnum, string> = {
  [ProductEffectiveRangeEnum.ONCE]: '一次性',
  [ProductEffectiveRangeEnum.ONE_CATEGORY]: '本车型',
  [ProductEffectiveRangeEnum.ALL_CATEGORY]: '所有车型'
};
export const productEffectiveRangeOptions = transformRecordToOption(productEffectiveRangeRecord, 'number');

export const MemberServicePackageStatusRecord: Record<number, string> = {
  1: '未使用',
  2: '已使用',
  3: '已失效'
};
export const MemberServicePackageStatusOptions = transformRecordToOption(MemberServicePackageStatusRecord, 'number');

export const vehicleTypeRecord: Record<number, string> = {
  1: '五座轿车或SUV',
  2: '六座及以上SUV或MPV'
};
export const vehicleTypeOptions = transformRecordToOption(vehicleTypeRecord, 'number');

export const caseTypeRecord: Record<number, string> = {
  1: '图片案例',
  2: '视频案例'
};
export const caseTypeOptions = transformRecordToOption(caseTypeRecord, 'number');

export const storeOutboundOrderTypeRecord: Record<number, string> = {
  1: '领料',
  2: '补料'
};
export const storeOutboundOrderTypeOptions = transformRecordToOption(storeOutboundOrderTypeRecord, 'number');

/** 推送消息类型 */
export const miniAppTypeRecord: Record<string, string> = {
  STORE_SALE_ORDER: '门店销售订单',
  STORE_OUTBOUND_ORDER: '门店仓库出库',
  STORE_ORDER_CANCEL_AUDIT: '订单作废待审核',
  STORE_ORDER_CANCEL_RESULT: '订单作废审核结果',
  FILM_PURCHASE_AUDIT: '采购订单审核',
  FILM_PURCHASE_RESULT: '采购订单审核结果',
  FILM_PURCHASE_ORDER: '采购订单',
  STORE_APPOINTMENT_INFO: '预约流程'
};
export const miniAppTypeOptions = transformRecordToOption(miniAppTypeRecord, 'string');

/** 小程序环境 */
export const miniProgramState: Record<string, string> = {
  DEVELOPER: '开发版',
  TRIAL: '体验版',
  FORMAL: '正式版'
};
export const miniProgramStateOptions = transformRecordToOption(miniProgramState, 'string');

/** 客户信息表状态 */
export const StoreCustomerStatusRecord: Record<StoreCustomerStatusEnum, string> = {
  [StoreCustomerStatusEnum.WAIT_APPOINTMENT]: '待预约',
  [StoreCustomerStatusEnum.APPOINTMENT_ONE]: '一次预约',
  [StoreCustomerStatusEnum.APPOINTMENT_SECOND]: '二次预约',
  [StoreCustomerStatusEnum.APPOINTMENT_THIRD]: '三次预约',
  [StoreCustomerStatusEnum.WAIT_COMPLETE_INFO]: '待完善',
  [StoreCustomerStatusEnum.WAIT_DISPATCH]: '待分配',
  [StoreCustomerStatusEnum.WAIT_AUDIT]: '待审核',
  [StoreCustomerStatusEnum.LOSS]: '已流失',
  // 已分配（仅用于展示，过滤需特殊处理）
  [StoreCustomerStatusEnum.DISPATCHED]: '已分配',
  [StoreCustomerStatusEnum.WAIT_STORE_ALLOCATION]: '待分配',
  [StoreCustomerStatusEnum.WAIT_STORE_APPOINTMENT]: '待预约',
  [StoreCustomerStatusEnum.ON_STORE_APPOINTMENT]: '预约中',
  [StoreCustomerStatusEnum.WAIT_STORE_VERIFIED]: '待核销',
  [StoreCustomerStatusEnum.IN_STORE_CONSTRUCTION]: '施工中',
  [StoreCustomerStatusEnum.STORE_LOSS]: '已流失',
  [StoreCustomerStatusEnum.DONE]: '已完成'
};
export const StoreCustomerStatusOptions = transformRecordToOption(StoreCustomerStatusRecord, 'number');

/** 客户预约信息表状态 */
export const AppointmentRecordStatusRecord: Record<AppointmentRecordStatusEnum, string> = {
  1: '成功预约',
  2: '流失',
  3: '待再次预约',
  4: '未接通'
};
export const AppointmentRecordStatusOptions = transformRecordToOption(AppointmentRecordStatusRecord, 'number');

/** 预约单状态对应的事件 */
export const StoreAppointmentEventRecord: Record<StoreAppointmentStateEnum, Api.Common.EventOption> = {
  [StoreAppointmentStateEnum.EXPIRED]: {
    config: null,
    type: null,
    roles: []
  },
  [StoreAppointmentStateEnum.LOSS]: {
    config: null,
    type: null,
    roles: []
  },
  [StoreAppointmentStateEnum.WAIT_AUDIT_LOSS]: {
    config: [
      {
        label: '通过',
        code: StoreAppointmentEventEnum.LOSS
      }
    ],
    type: null,
    roles: []
  },
  [StoreAppointmentStateEnum.WAIT_AUDIT_CANCEL]: {
    config: null,
    type: null,
    roles: []
  },
  [StoreAppointmentStateEnum.WAIT_ALLOCATION]: {
    config: null,
    type: null,
    roles: []
  },
  [StoreAppointmentStateEnum.WAIT_APPOINTMENT]: {
    config: null,
    type: null,
    roles: []
  },
  [StoreAppointmentStateEnum.ON_APPOINTMENT]: {
    config: null,
    type: null,
    roles: []
  },
  [StoreAppointmentStateEnum.WAIT_AUDIT_EDIT]: {
    config: null,
    type: null,
    roles: []
  },
  [StoreAppointmentStateEnum.WAIT_VERIFIED]: {
    config: null,
    type: null,
    roles: []
  },
  [StoreAppointmentStateEnum.IN_CONSTRUCTION]: {
    config: null,
    type: null,
    roles: []
  },
  [StoreAppointmentStateEnum.DONE]: {
    config: null,
    type: null,
    roles: []
  }
};

/** 预约单流失原因 */
export const AppointmentLossReason: Record<number, string> = {
  1: '预约流失',
  2: '到店流失'
};
export const AppointmentLossReasonOptions = transformRecordToOption(AppointmentLossReason, 'number');

/** U8客户类别 */
export const U8CCCodeRecord: Record<U8CCCodeEnum, string> = {
  [U8CCCodeEnum.TH_05]: '隐形车衣',
  [U8CCCodeEnum.TH_07]: '窗膜',
  [U8CCCodeEnum.TH_99]: '其他'
};
export const U8CCCodeOptions = transformRecordToOption(U8CCCodeRecord, 'string');

/** 消息通知类型 */
export const NotificationTypeRecord: Record<NotificationTypeEnum, CommonType.TagOption<number>> = {
  [NotificationTypeEnum.SYSTEM]: {
    label: '系统通知',
    config: {
      color: '#1f6dff'
    }
  },
  [NotificationTypeEnum.SALE_ORDER]: {
    label: '销售订单',
    config: {
      color: '#FFAC37'
    }
  },
  [NotificationTypeEnum.FILM_PURCHASE_ORDER]: {
    label: '车模采购订单',
    config: {
      color: '#9ccc65'
    }
  },
  [NotificationTypeEnum.STORE_PURCHASE_ORDER]: {
    label: '商品采购订单',
    config: {
      color: '#d4e157'
    }
  },
  [NotificationTypeEnum.STORE_CUSTOMER_TRANSFER]: {
    label: '大客户流转',
    config: {
      color: '#ff5722'
    }
  },
  [NotificationTypeEnum.STORE_APPOINTMENT]: {
    label: '客户预约',
    config: {
      color: '#ef5350'
    }
  },
  [NotificationTypeEnum.PRODUCT_INVENTORY]: {
    label: '商品库存',
    config: {
      color: '#009688'
    }
  },
  [NotificationTypeEnum.FILM_INVENTORY]: {
    label: '车膜库存',
    config: {
      color: '#00acc1'
    }
  }
};
export const NotificationTypeOptions = transformRecordToOption(NotificationTypeRecord, 'number');

/** 消息通知动作 */
export const NotificationActionRecord: Record<NotificationActionEnum, string> = {
  [NotificationActionEnum.TODO]: '待办',
  [NotificationActionEnum.NOTIFY]: '通知'
};
export const NotificationActionOptions = transformRecordToOption(NotificationActionRecord, 'number');
